// Phase 9B rebuild — Manager roster planner (hybrid week + selected-day).
//
// Rebuild scope: the previous multi-view planner (EmployeeMatrix + DayGrid
// + WeekCalendar) has been retired. This page is now one coherent hybrid
// experience per the rebuild spec:
//
//   * WeekStrip: 7 compact day cards, "at a glance" scheduled counts
//                and exception badges only. Selecting a day drives detail.
//   * DayAgenda: chronological, time-axis-on-the-left detail of the
//                selected day, with SHIFT GROUPS instead of employee lanes.
//                20 employees on the same 08:00–16:00 shift render as ONE
//                calm grouped card — never 20 narrow lanes.
//   * ShiftDrawer: contextual editor for create / edit / duplicate.
//   * Sticky publish bar: the SOLE primary publish action when drafts exist.
//
// Mobile falls through to the existing MobileRoster which already does
// selected-day-only. The desktop hybrid is unified: no view toggle, no
// employee matrix, no directory rows.
import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import api from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import { useI18n } from "@/i18n/I18nContext";
import { PageHeader } from "@/components/ui-bits";
import {
  ChevronLeft, ChevronRight, Plus, Send, Filter, Copy, ChevronDown, MoreHorizontal, X,
  Calendar as CalIcon, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import CopyDaysModal from "@/components/CopyDaysModal";
import ShiftConflictModal from "@/components/ShiftConflictModal";
import MobileRoster from "@/components/MobileRoster";
import LocationScopeSelect from "@/components/LocationScopeSelect";
import WeekStrip from "@/components/roster/WeekStrip";
import DayAgenda from "@/components/roster/DayAgenda";
import ShiftDrawer from "@/components/roster/ShiftDrawer";
import ConfirmDialog from "@/components/ConfirmDialog";
import { toastWithUndo } from "@/lib/undo";
import useTodayISO from "@/lib/useTodayISO";

/* ---------- Date helpers ---------- */
function startOfWeek(d) {
  const x = new Date(d); const day = (x.getDay() + 6) % 7;
  x.setDate(x.getDate() - day); x.setHours(0, 0, 0, 0); return x;
}
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
function fmtDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function shiftHours(start, end) {
  if (!start || !end) return 0;
  const [sh, sm] = start.split(":").map(Number);
  const [eh, em] = end.split(":").map(Number);
  let mins = (eh * 60 + em) - (sh * 60 + sm);
  if (mins < 0) mins += 24 * 60;
  return Math.round((mins / 60) * 10) / 10;
}

const emptyShift = {
  date: "", start_time: "", end_time: "",
  location_id: "", team_id: "", employee_id: "", role_label: "", status: "draft", notes: "",
  pause_minutes: null,
};

function computeAutoPause(hours, settings) {
  if (!settings || !settings.auto_pause_enabled) return 0;
  if (settings.auto_pause_mode === "tiered") {
    const tiers = settings.auto_pause_tiers || [];
    let best = 0, bestH = -1;
    for (const t of tiers) {
      const h = Number(t.hours) || 0;
      const m = Number(t.minutes) || 0;
      if (hours >= h && h > bestH) { bestH = h; best = m; }
    }
    return best;
  }
  const th = Number(settings.auto_pause_threshold_hours) || 0;
  if (hours >= th && th > 0) return Number(settings.auto_pause_flat_minutes) || 0;
  return 0;
}

export default function Roster() {
  const { t } = useI18n();
  const { user: currentUser } = useAuth();
  const isOwner = currentUser?.role === "owner";
  const todayISO = useTodayISO();
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  const [shifts, setShifts] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [locations, setLocations] = useState([]);
  const [teams, setTeams] = useState([]);
  const [locFilter, setLocFilter] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyShift);
  const [repeatWeeks, setRepeatWeeks] = useState(0);
  const [showCopyMenu, setShowCopyMenu] = useState(false);
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [selectedDateISO, setSelectedDateISO] = useState(() => {
    const t = new Date();
    return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, "0")}-${String(t.getDate()).padStart(2, "0")}`;
  });
  const [conflict, setConflict] = useState({ open: false, mode: "overlap", conflicts: [], skipped: [], created: 0 });
  const [companySettings, setCompanySettings] = useState(null);
  const [pauseWarning, setPauseWarning] = useState(null);
  const [confirmDelShift, setConfirmDelShift] = useState(false);
  const [confirmPublish, setConfirmPublish] = useState(false);

  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia("(max-width: 767px)").matches : false
  );
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(max-width: 767px)");
    const handler = (e) => setIsMobile(e.matches);
    try { mq.addEventListener("change", handler); } catch { mq.addListener(handler); }
    return () => {
      try { mq.removeEventListener("change", handler); } catch { mq.removeListener(handler); }
    };
  }, []);

  const [dayIdx, setDayIdx] = useState(() => {
    const now = new Date();
    const start = startOfWeek(now);
    const idx = Math.floor((now - start) / (24 * 3600 * 1000));
    return Math.max(0, Math.min(6, idx));
  });

  // Follow midnight rollover when user is on "today".
  const prevTodayRef = useMemo(() => ({ iso: todayISO }), []); // eslint-disable-line
  useEffect(() => {
    const prev = prevTodayRef.iso;
    if (prev === todayISO) return;
    prevTodayRef.iso = todayISO;
    if (selectedDateISO === prev) setSelectedDateISO(todayISO);
  }, [todayISO, selectedDateISO, prevTodayRef]);

  const parseConflictError = (err) => {
    const status = err?.response?.status;
    const detail = err?.response?.data?.detail;
    if (status === 409 && detail && typeof detail === "object" && detail.code === "shift_overlap") {
      return detail.conflicts || [];
    }
    return null;
  };

  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);
  const startISO = fmtDate(days[0]);
  const endISO = fmtDate(days[6]);

  // Keep selectedDateISO inside the visible week when user navigates weeks.
  useEffect(() => {
    if (!selectedDateISO) return;
    if (selectedDateISO < startISO || selectedDateISO > endISO) {
      setSelectedDateISO(startISO);
    }
  }, [startISO, endISO, selectedDateISO]);

  const load = async () => {
    const q = new URLSearchParams({ start: startISO, end: endISO });
    if (locFilter) q.set("location_id", locFilter);
    const [s, e, l, tm] = await Promise.all([
      api.get(`/shifts?${q.toString()}`),
      api.get("/users?role=employee"),
      api.get("/locations"),
      api.get("/teams"),
    ]);
    setShifts(s.data); setEmployees(e.data); setLocations(l.data); setTeams(tm.data);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [startISO, endISO, locFilter]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/company/settings");
        setCompanySettings(data);
      } catch { /* non-critical */ }
    })();
  }, []);

  const openNew = (date, employeeId = "", approxTime = null) => {
    setEditing(null);
    // No hardcoded 08:00/17:00 defaults (spec §6, §28, §29). Prefill only
    // when the planner clicked a specific time slot; otherwise leave times
    // empty so the user fills real business hours.
    let start = approxTime || "";
    let end = "";
    if (approxTime) {
      const [h, m] = approxTime.split(":").map(Number);
      const eh = String(Math.min(23, h + 4)).padStart(2, "0");
      end = `${eh}:${String(m).padStart(2, "0")}`;
    }
    setForm({
      ...emptyShift,
      date,
      start_time: start,
      end_time: end,
      location_id: locFilter || (locations.length === 1 ? locations[0]?._id : "") || "",
      team_id: "",
      employee_id: employeeId || "",
    });
    setShowModal(true);
  };
  const openEdit = (s) => {
    setEditing(s);
    setForm({
      date: s.date, start_time: s.start_time, end_time: s.end_time,
      location_id: s.location_id || "", team_id: s.team_id || "",
      employee_id: s.employee_id || "", role_label: s.role_label || "",
      status: s.status, notes: s.notes || "",
      pause_minutes: typeof s.pause_minutes === "number" ? s.pause_minutes : null,
    });
    setShowModal(true);
  };
  // Group click: for a single-shift group open that shift directly. For
  // multi-shift groups (e.g. 20 employees on the same 08:00–16:00) we open
  // the first shift — a full group-detail drawer with per-employee remove
  // is a future refinement.
  const openGroup = (group) => {
    if (!group || !group.shifts || group.shifts.length === 0) return;
    openEdit(group.shifts[0]);
  };

  const persistShift = async (opts = {}) => {
    const payload = { ...form };
    if (!payload.employee_id) payload.employee_id = null;
    if (!payload.location_id) payload.location_id = null;
    if (!payload.team_id) payload.team_id = null;
    if (payload.pause_minutes === null || payload.pause_minutes === "") {
      delete payload.pause_minutes;
    } else {
      payload.pause_minutes = Number(payload.pause_minutes);
    }
    try {
      if (editing) await api.patch(`/shifts/${editing._id}`, payload);
      else await api.post(`/shifts${repeatWeeks > 0 ? `?repeat_weeks=${repeatWeeks}` : ""}`, payload);
      toast.success(repeatWeeks > 0 && !editing ? `${t("saved")} · ${repeatWeeks + 1} ${t("shifts_short")}` : t("saved"));
      if (opts.keepOpen && !editing) {
        setForm({ ...emptyShift, date: payload.date, location_id: payload.location_id || locations[0]?._id || "" });
        setRepeatWeeks(0);
        setPauseWarning(null);
        load();
      } else {
        setShowModal(false); setRepeatWeeks(0); setPauseWarning(null); load();
      }
    } catch (e) {
      const conflicts = parseConflictError(e);
      if (conflicts) {
        setConflict({ open: true, mode: "overlap", conflicts, skipped: [], created: 0 });
      } else {
        toast.error(e.response?.data?.detail || t("error"));
      }
    }
  };

  const save = async (opts = {}) => {
    const hours = shiftHours(form.start_time, form.end_time);
    if (hours <= 0 || hours > 24) {
      toast.error(t("shift_max_length_error"));
      return;
    }
    const explicit = form.pause_minutes !== null && form.pause_minutes !== "" ? Number(form.pause_minutes) : null;
    const effective = explicit !== null ? explicit : computeAutoPause(hours, companySettings);
    const ratio = companySettings?.pause_warning_ratio_percent ?? 5;
    const requiredMinutes = (ratio / 100) * hours * 60;
    if (ratio > 0 && effective < requiredMinutes) {
      setPauseWarning({ hours, minutes: effective, keepOpen: !!opts.keepOpen });
      return;
    }
    await persistShift(opts);
  };
  const saveAndAddAnother = () => save({ keepOpen: true });

  const remove = () => { if (editing) setConfirmDelShift(true); };
  const doRemove = async () => {
    if (!editing) return;
    const snap = {
      date: editing.date, start_time: editing.start_time, end_time: editing.end_time,
      location_id: editing.location_id || null, team_id: editing.team_id || null,
      employee_id: editing.employee_id || null, role_label: editing.role_label || "",
      status: editing.status, notes: editing.notes || "",
    };
    if (typeof editing.pause_minutes === "number") snap.pause_minutes = editing.pause_minutes;
    setConfirmDelShift(false);
    setShowModal(false);
    try {
      await api.delete(`/shifts/${editing._id}`);
      load();
      toastWithUndo({
        message: t("shift_deleted"),
        undoLabel: t("undo"),
        onUndo: async () => {
          const payload = { ...snap };
          if (payload.pause_minutes === undefined) delete payload.pause_minutes;
          await api.post(`/shifts`, payload);
          load();
        },
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || t("error"));
    }
  };

  const publishWeek = async () => {
    try {
      await api.post(`/shifts/publish-week?start=${startISO}&end=${endISO}`);
      toast.success(`${t("published")} · ${startISO} → ${endISO}`);
      setConfirmPublish(false);
      load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : (detail?.message || t("publish_failed") || t("error"));
      toast.error(msg);
    }
  };
  const requestPublishWeek = () => setConfirmPublish(true);

  const copyPrevWeek = async () => {
    const prevStart = fmtDate(addDays(weekStart, -7));
    const prevEnd = fmtDate(addDays(weekStart, -1));
    try {
      const { data } = await api.post(
        `/shifts/copy-week?source_start=${prevStart}&source_end=${prevEnd}&target_start=${startISO}`
      );
      const skipped = data.skipped || [];
      if (skipped.length > 0) setConflict({ open: true, mode: "partial", conflicts: [], skipped, created: data.created });
      else toast.success(`${data.created} ${t("copied_shifts")}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || t("error")); }
    setShowCopyMenu(false);
  };

  const copyThisToNextWeek = async () => {
    const nextStart = fmtDate(addDays(weekStart, 7));
    try {
      const { data } = await api.post(
        `/shifts/copy-week?source_start=${startISO}&source_end=${endISO}&target_start=${nextStart}`
      );
      const skipped = data.skipped || [];
      if (skipped.length > 0) setConflict({ open: true, mode: "partial", conflicts: [], skipped, created: data.created });
      else toast.success(`${data.created} ${t("copied_shifts")}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || t("error")); }
    setShowCopyMenu(false);
  };

  const duplicateShift = async () => {
    if (!editing) return;
    try {
      await api.post(`/shifts/${editing._id}/duplicate?days=1`);
      toast.success(t("shift_duplicated"));
      setShowModal(false);
      load();
    } catch (e) {
      const conflicts = parseConflictError(e);
      if (conflicts) setConflict({ open: true, mode: "overlap", conflicts, skipped: [], created: 0 });
      else toast.error(e.response?.data?.detail || t("error"));
    }
  };

  const shiftsByDate = useMemo(() => {
    const m = {};
    for (const s of shifts) (m[s.date] = m[s.date] || []).push(s);
    return m;
  }, [shifts]);
  const daySelected = shiftsByDate[selectedDateISO] || [];
  const draftShifts = shifts.filter((s) => s.status !== "published").length;

  const weekLabel = `${days[0].toLocaleDateString("nl", { day: "numeric", month: "short" })} – ${days[6].toLocaleDateString("nl", { day: "numeric", month: "short", year: "numeric" })}`;

  const hasDrafts = draftShifts > 0;

  return (
    <div data-testid="page-roster" className={`max-w-[1400px] mx-auto ${hasDrafts ? "pb-24" : "pb-8"}`}>
      <PageHeader
        eyebrow={t("roster")}
        title={weekLabel}
        subtitle={null}
        actions={
          <div className="hidden md:flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-0.5 bg-[hsl(var(--surface))] border hairline rounded-full p-1 shadow-[var(--shadow-xs)]">
              <button data-testid="week-prev" onClick={() => setWeekStart(addDays(weekStart, -7))} className="w-9 h-9 rounded-full hover:bg-[hsl(var(--muted))] grid place-items-center transition-colors" aria-label={t("previous_week") || "Vorige week"}>
                <ChevronLeft size={16} strokeWidth={2} />
              </button>
              <button data-testid="week-today" onClick={() => { setWeekStart(startOfWeek(new Date())); setSelectedDateISO(todayISO); }} className="px-4 h-9 rounded-full hover:bg-[hsl(var(--muted))] transition-colors text-[13px] font-semibold">
                {t("today")}
              </button>
              <button data-testid="week-next" onClick={() => setWeekStart(addDays(weekStart, 7))} className="w-9 h-9 rounded-full hover:bg-[hsl(var(--muted))] grid place-items-center transition-colors" aria-label={t("next_week") || "Volgende week"}>
                <ChevronRight size={16} strokeWidth={2} />
              </button>
            </div>
            {locations.length > 1 && (
              <LocationScopeSelect
                locations={locations}
                value={locFilter}
                onChange={setLocFilter}
                allowAll={isOwner}
                allLabel={t("all_locations")}
                testid="filter-location"
                ariaLabel={t("location")}
              />
            )}
            <div className="relative">
              <button
                onClick={() => setShowCopyMenu((v) => !v)}
                data-testid="more-menu-btn"
                className="btn btn-ghost"
                aria-haspopup="menu"
                aria-expanded={showCopyMenu}
              >
                <MoreHorizontal size={14} strokeWidth={2} /> {t("more") || "Meer"}
                <ChevronDown size={12} strokeWidth={2.5} className={`transition-transform ${showCopyMenu ? "rotate-180" : ""}`} />
              </button>
              <AnimatePresence>
                {showCopyMenu && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setShowCopyMenu(false)} />
                    <motion.div
                      initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
                      className="absolute right-0 top-full mt-2 w-72 card p-1.5 z-50"
                      style={{ boxShadow: "var(--shadow-lg)" }}
                      data-testid="more-menu"
                      role="menu"
                    >
                      <div className="px-3 py-1.5 text-[10.5px] font-bold uppercase tracking-wider text-[hsl(var(--text-muted))]">
                        {t("copy_menu") || "Kopiëren"}
                      </div>
                      <button onClick={copyPrevWeek} className="w-full flex items-start gap-3 p-3 rounded-xl hover:bg-[hsl(var(--muted))] transition-colors text-left" data-testid="copy-prev-week" role="menuitem">
                        <ChevronLeft size={14} className="mt-0.5 text-[hsl(var(--primary))]" />
                        <div>
                          <div className="text-[13px] font-semibold text-[hsl(var(--text-strong))]">{t("copy_prev_week")}</div>
                          <div className="text-[11px] text-[hsl(var(--text-muted))] mt-0.5">{t("copy_prev_week_hint")}</div>
                        </div>
                      </button>
                      <button onClick={copyThisToNextWeek} className="w-full flex items-start gap-3 p-3 rounded-xl hover:bg-[hsl(var(--muted))] transition-colors text-left" data-testid="copy-to-next-week" role="menuitem">
                        <ChevronRight size={14} className="mt-0.5 text-[hsl(var(--primary))]" />
                        <div>
                          <div className="text-[13px] font-semibold text-[hsl(var(--text-strong))]">{t("copy_to_next_week")}</div>
                          <div className="text-[11px] text-[hsl(var(--text-muted))] mt-0.5">{t("copy_to_next_week_hint")}</div>
                        </div>
                      </button>
                      <button onClick={() => { setShowCopyMenu(false); setShowCopyModal(true); }} className="w-full flex items-start gap-3 p-3 rounded-xl hover:bg-[hsl(var(--muted))] transition-colors text-left" data-testid="copy-open-picker" role="menuitem">
                        <CalIcon size={14} className="mt-0.5 text-[hsl(var(--primary))]" />
                        <div>
                          <div className="text-[13px] font-semibold text-[hsl(var(--text-strong))]">{t("copy_pick_days")}</div>
                          <div className="text-[11px] text-[hsl(var(--text-muted))] mt-0.5">{t("copy_pick_days_hint")}</div>
                        </div>
                      </button>
                      {!hasDrafts && (
                        <>
                          <div className="h-px bg-[hsl(var(--border))] my-1" />
                          <button onClick={() => { setShowCopyMenu(false); requestPublishWeek(); }} className="w-full flex items-start gap-3 p-3 rounded-xl hover:bg-[hsl(var(--muted))] transition-colors text-left" data-testid="menu-publish" role="menuitem">
                            <Send size={14} className="mt-0.5 text-[hsl(var(--primary))]" />
                            <div>
                              <div className="text-[13px] font-semibold text-[hsl(var(--text-strong))]">{t("publish_week")}</div>
                              <div className="text-[11px] text-[hsl(var(--text-muted))] mt-0.5">{t("publish_up_to_date_hint") || "Alle wijzigingen zijn al gepubliceerd."}</div>
                            </div>
                          </button>
                        </>
                      )}
                    </motion.div>
                  </>
                )}
              </AnimatePresence>
            </div>
            <button onClick={() => openNew(selectedDateISO || fmtDate(days[0]))} data-testid="add-shift" className="btn btn-primary">
              <Plus size={16} strokeWidth={2} />{t("add_shift")}
            </button>
          </div>
        }
      />
      {/* Active filter chip — only visible when a filter is applied. */}
      {locFilter && (
        <div className="hidden md:flex items-center gap-2 mb-3" data-testid="active-filter-chips">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-[hsl(var(--text-muted))]">
            {t("filters_active") || "Actief filter"}
          </span>
          <span
            className="inline-flex items-center gap-1.5 pl-2.5 pr-1 py-1 rounded-full border hairline text-[12px] font-semibold bg-[hsl(var(--primary-soft))] text-[hsl(var(--primary))]"
            data-testid="filter-chip-location"
          >
            {locations.find((l) => l._id === locFilter)?.name || "—"}
            <button
              type="button"
              onClick={() => setLocFilter("")}
              className="w-5 h-5 grid place-items-center rounded-full hover:bg-[hsl(var(--primary))]/10"
              aria-label={t("clear_filter") || "Filter wissen"}
              data-testid="filter-chip-clear"
            >
              <X size={11} strokeWidth={2.5} />
            </button>
          </span>
        </div>
      )}

      {/* Roster hybrid layout — mobile falls through to MobileRoster. */}
      {isMobile ? (
        <MobileRoster
          days={days}
          dayIdx={dayIdx}
          setDayIdx={setDayIdx}
          shiftsByDate={shiftsByDate}
          hoursByDay={{}}
          employees={employees}
          locations={locations}
          openEdit={openEdit}
          openNew={openNew}
          onWeekShift={(dir) => setWeekStart(addDays(weekStart, dir * 7))}
          t={t}
        />
      ) : (
        <>
          <WeekStrip
            days={days}
            shifts={shifts}
            selectedDate={selectedDateISO}
            onSelect={(k) => setSelectedDateISO(k)}
            t={t}
          />
          <DayAgenda
            selectedDate={selectedDateISO}
            dayShifts={daySelected}
            employeesById={Object.fromEntries(employees.map((e) => [e._id, e]))}
            locations={locations}
            teams={teams}
            onOpenShift={openEdit}
            onOpenNew={openNew}
            t={t}
          />
        </>
      )}

      {/* Feb 2026 — DayAgenda renders its own contextual empty state per day,
          so the outer generic "empty_roster" block was showing a second empty
          card underneath. Removed to eliminate the duplicate empty-state UI. */}

      <ShiftDrawer
        open={showModal}
        editing={editing}
        form={form}
        setForm={setForm}
        employees={employees}
        locations={locations}
        teams={teams}
        allWeekShifts={shifts}
        isOwner={isOwner}
        currentUser={currentUser}
        repeatWeeks={repeatWeeks}
        setRepeatWeeks={setRepeatWeeks}
        onSave={save}
        onSaveAndAddAnother={saveAndAddAnother}
        onDelete={remove}
        onDuplicate={duplicateShift}
        onClose={() => setShowModal(false)}
        t={t}
      />

      <CopyDaysModal
        open={showCopyModal}
        onClose={() => setShowCopyModal(false)}
        weekStart={weekStart}
        shiftsByDate={shiftsByDate}
        onDone={load}
        onConflicts={(created, skipped) => {
          if (skipped && skipped.length > 0) setConflict({ open: true, mode: "partial", conflicts: [], skipped, created });
        }}
      />

      <ShiftConflictModal
        open={conflict.open}
        mode={conflict.mode}
        conflicts={conflict.conflicts}
        skipped={conflict.skipped}
        created={conflict.created}
        onClose={() => setConflict({ ...conflict, open: false })}
      />

      <ConfirmDialog
        open={confirmDelShift}
        title={t("confirm_delete_title")}
        body={t("confirm_delete_shift")}
        confirmLabel={t("delete")}
        onConfirm={doRemove}
        onCancel={() => setConfirmDelShift(false)}
        testid="confirm-shift"
      />

      <ConfirmDialog
        open={confirmPublish}
        title={t("publish_confirm_title") || "Rooster publiceren?"}
        body={(t("publish_confirm_body_v2") || "Je publiceert de wijzigingen voor {range}. Medewerkers zien daarna het bijgewerkte rooster.").replace("{range}", weekLabel)}
        confirmLabel={t("publish_week") || "Rooster publiceren"}
        onConfirm={publishWeek}
        onCancel={() => setConfirmPublish(false)}
        testid="confirm-publish"
      />

      {/* Auto-pause warning modal */}
      <AnimatePresence>
        {pauseWarning && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm grid place-items-center p-4"
            onClick={() => setPauseWarning(null)}
            data-testid="pause-warning-modal"
          >
            <motion.div
              initial={{ scale: 0.94, opacity: 0, y: 12 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.94, opacity: 0 }}
              transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
              className="card w-full max-w-md p-6"
              onClick={(e) => e.stopPropagation()}
              style={{ boxShadow: "var(--shadow-xl)" }}
            >
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl bg-[hsl(var(--warning-soft,45_100%_92%))] text-[hsl(var(--warning))] grid place-items-center shrink-0">
                  <AlertTriangle size={18} strokeWidth={2} />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-display text-[19px] font-bold tracking-tight">{t("pause_warning_title")}</h3>
                  <p className="text-[13px] text-[hsl(var(--text-muted))] mt-1 leading-relaxed">
                    {(t("pause_warning_body_v2") || "Deze dienst duurt {hours}u maar er staat maar {minutes} min pauze in.")
                      .replace("{hours}", pauseWarning.hours + "u")
                      .replace("{minutes}", pauseWarning.minutes)}
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-6">
                <button onClick={() => setPauseWarning(null)} className="btn btn-ghost" data-testid="pause-warning-cancel">
                  {t("pause_warning_cancel_v2") || "Pauze aanpassen"}
                </button>
                <button onClick={persistShift} className="btn btn-primary" data-testid="pause-warning-confirm">
                  {t("pause_warning_confirm_v2") || "Zo laten, opslaan"}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Sticky publish bar — the SOLE primary publish action when drafts exist. */}
      {hasDrafts && (
        <div
          data-testid="sticky-publish-bar"
          className="fixed inset-x-0 bottom-0 z-30 bg-[hsl(var(--surface))] border-t hairline shadow-[0_-8px_24px_-8px_rgba(0,0,0,0.15)]"
        >
          <div className="max-w-[1400px] mx-auto px-4 py-3 flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <span
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11.5px] font-bold tabular-nums"
                style={{
                  background: "hsl(var(--scheduled-draft-soft, var(--warning-soft)))",
                  color: "hsl(var(--scheduled-draft, var(--warning)))",
                }}
                data-testid="sticky-publish-count"
              >
                {draftShifts} {draftShifts === 1 ? (t("unpublished_single") || "niet-gepubliceerde wijziging") : (t("unpublished_changes") || "niet-gepubliceerde wijzigingen")}
              </span>
              <span className="text-[12.5px] text-[hsl(var(--text-muted))] truncate">
                {t("sticky_publish_hint") || "Medewerkers zien nog het eerder gepubliceerde rooster."}
              </span>
            </div>
            <button
              onClick={requestPublishWeek}
              data-testid="sticky-publish-btn"
              className="btn btn-primary"
            >
              <Send size={14} strokeWidth={2} />
              {t("publish_week")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
